"use client";

/**
 * Formulaire de capture de lead « DEVIS TRANSFERT » (B2C bas-de-funnel).
 *
 * Surface de conversion ON-PAGE des pages trajet (/chauffeur/transferts/[trajet]
 * et /en/chauffeur/transfers/[route]). Ces pages rankent sur des requêtes
 * « compact » à forte intention (« prix transfert Cannes Monaco ») et montrent
 * un vrai tableau de tarifs forfaitaires — mais la seule conversion était un
 * lien hero vers /reservation. Ici le lead chaud est capturé SUR la page,
 * pré-rempli avec le trajet exact (départ → arrivée).
 *
 * Fin wrapper au-dessus de <LeadForm> (coquille + pipeline d'envoi PARTAGÉS) :
 * validation client (react-hook-form), honeypot anti-spam, locale dans le body,
 * POST vers /api/contact, états loading / success / error. Aucune nouvelle
 * mécanique de fetch. Seuls les champs (trajet + course + coordonnées), le
 * sujet/message et la charge utile transfert (pickup/dropoff/date/time/
 * passengers) sont fournis ici ; tout le reste est mutualisé.
 *
 * Bilingue via la prop `locale` (défaut "fr"). Toutes les chaînes visibles
 * viennent de TRANSFER_UI[locale].quoteForm. Le prix de réassurance affiché est
 * lu depuis getRoutePrices(route) — jamais en dur.
 *
 * Drapeau transfert : locationType = "transfert" (détecté côté API pour préfixer
 * le sujet équipe). Ancre de section : id={TRANSFER_QUOTE_FORM_ID} ("devis"),
 * importée depuis transferUi.ts (module neutre — règle RSC).
 */
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import {
  getRoutePrices,
  getRoutePlaces,
  getRouteLabel,
  type TransferRoute,
} from "@/data/transfers";
import { placeShortName } from "@/data/transfers/places";
import { PHONE_PATTERNS } from "@/config/forms";
import LeadForm, {
  LEAD_INPUT_CLASS,
  LEAD_LABEL_CLASS,
} from "@/components/forms/LeadForm";
import { TRANSFER_UI, TRANSFER_QUOTE_FORM_ID } from "./transferUi";

type TransferQuoteFormData = {
  pickup: string;
  dropoff: string;
  date: string;
  time: string;
  passengers: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  honeypot?: string;
};

export default function TransferQuoteForm({
  locale = "fr",
  route,
}: {
  locale?: Locale;
  route: TransferRoute;
}): JSX.Element {
  const ui = tr(TRANSFER_UI, locale).quoteForm;

  // Lieux du trajet pour pré-remplir départ / arrivée (locale-aware) et
  // composer un sujet équipe FR (l'équipe lit le FR, quelle que soit la langue
  // du visiteur).
  const { from, to } = getRoutePlaces(route);
  const pickupDefault = placeShortName(from, locale);
  const dropoffDefault = placeShortName(to, locale);
  const routeLabelFr = getRouteLabel(route, "fr");

  // Prix mini réel du trajet (réassurance « à partir de X € »), lu d'infos.ts.
  const prices = getRoutePrices(route);
  const minPrice =
    prices.length > 0 ? Math.min(...prices.map((p) => p.price)) : null;

  const inputClass = LEAD_INPUT_CLASS;
  const labelClass = LEAD_LABEL_CLASS;

  const defaultValues: TransferQuoteFormData = {
    pickup: pickupDefault,
    dropoff: dropoffDefault,
    date: "",
    time: "",
    passengers: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    honeypot: "",
  };

  return (
    <LeadForm<TransferQuoteFormData>
      locale={locale}
      anchorId={TRANSFER_QUOTE_FORM_ID}
      submitIcon="mdi:calendar-check"
      honeypotId="transfer-quote-honeypot"
      defaultValues={defaultValues}
      resetValues={defaultValues}
      ui={{
        eyebrow: ui.eyebrow,
        heading: ui.heading,
        subtitle: ui.subtitle,
        honeypotLabel: ui.honeypotLabel,
        submit: ui.submit,
        sending: ui.sending,
        successMessage: ui.successMessage,
        errorMessage: ui.errorMessage,
        consentNote: ui.consentNote,
        privacyNote: ui.privacyNote,
      }}
      intro={
        // Récap trajet + réassurance prix mini (lu d'infos.ts).
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 font-medium text-black">
            <Icon icon="mdi:map-marker-path" className="h-4 w-4 text-primary" />
            {pickupDefault} → {dropoffDefault}
          </span>
          {minPrice != null && (
            <span className="inline-flex items-center gap-1.5 font-semibold text-primary">
              <Icon icon="mdi:tag-outline" className="h-4 w-4" />
              {ui.fromPrice(`${minPrice} €`)}
            </span>
          )}
        </div>
      }
      buildSubmission={(values) => {
        const pickup = values.pickup.trim() || pickupDefault;
        const dropoff = values.dropoff.trim() || dropoffDefault;
        const date = values.date.trim();
        const time = values.time.trim();
        const passengers = values.passengers.trim();

        // Sujet équipe (FR) : libellé du trajet FR quelle que soit la locale.
        const subject = `Devis transfert — ${routeLabelFr}`;

        // Message : saisie libre, sinon défaut sensé (≥ 5 car. requis côté API).
        const typedMessage = values.message.trim();
        const message =
          typedMessage.length >= 5
            ? typedMessage
            : ui.defaultMessage({ pickup, dropoff, date, time, passengers });

        return {
          subject,
          message,
          locationType: "transfert",
          extraPayload: {
            pickup,
            dropoff,
            date: date || undefined,
            time: time || undefined,
            passengers: passengers || undefined,
          },
        };
      }}
      fields={(register, errors) => (
        <>
          {/* Départ (pré-rempli, éditable) */}
          <label className="block">
            <span className={labelClass}>{ui.pickupLabel}</span>
            <input
              className={inputClass}
              placeholder={ui.pickupPlaceholder}
              {...register("pickup")}
            />
          </label>

          {/* Arrivée (pré-rempli, éditable) */}
          <label className="block">
            <span className={labelClass}>{ui.dropoffLabel}</span>
            <input
              className={inputClass}
              placeholder={ui.dropoffPlaceholder}
              {...register("dropoff")}
            />
          </label>

          {/* Date (requise) */}
          <label className="block">
            <span className={labelClass}>{ui.dateLabel}</span>
            <input
              type="date"
              className={inputClass}
              {...register("date", { required: ui.requiredDate })}
            />
            {errors.date && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.date.message}
              </span>
            )}
          </label>

          {/* Heure */}
          <label className="block">
            <span className={labelClass}>{ui.timeLabel}</span>
            <input type="time" className={inputClass} {...register("time")} />
          </label>

          {/* Passagers */}
          <label className="block">
            <span className={labelClass}>{ui.passengersLabel}</span>
            <select className={inputClass} {...register("passengers")}>
              <option value="">{ui.passengersPlaceholder}</option>
              {["1", "2", "3", "4", "5", "6", "7", "8+"].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          {/* Email */}
          <label className="block">
            <span className={labelClass}>{ui.emailLabel}</span>
            <input
              type="email"
              className={inputClass}
              placeholder={ui.emailPlaceholder}
              {...register("email", { required: ui.requiredEmail })}
            />
            {errors.email && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.email.message}
              </span>
            )}
          </label>

          {/* Prénom */}
          <label className="block">
            <span className={labelClass}>{ui.firstNameLabel}</span>
            <input
              className={inputClass}
              placeholder={ui.firstNamePlaceholder}
              {...register("firstName", {
                required: ui.requiredFirstName,
                minLength: { value: 2, message: ui.requiredFirstName },
              })}
            />
            {errors.firstName && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.firstName.message}
              </span>
            )}
          </label>

          {/* Nom */}
          <label className="block">
            <span className={labelClass}>{ui.lastNameLabel}</span>
            <input
              className={inputClass}
              placeholder={ui.lastNamePlaceholder}
              {...register("lastName", {
                required: ui.requiredLastName,
                minLength: { value: 2, message: ui.requiredLastName },
              })}
            />
            {errors.lastName && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.lastName.message}
              </span>
            )}
          </label>

          {/* Téléphone (international, facultatif) */}
          <label className="block">
            <span className={labelClass}>{ui.phoneLabel}</span>
            <input
              type="tel"
              className={inputClass}
              placeholder={ui.phonePlaceholder}
              {...register("phone", {
                pattern: {
                  value: PHONE_PATTERNS.intl,
                  message: ui.invalidPhone,
                },
              })}
            />
            {errors.phone && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.phone.message}
              </span>
            )}
          </label>

          {/* Message (facultatif) */}
          <label className="block md:col-span-2">
            <span className={labelClass}>{ui.messageLabel}</span>
            <textarea
              rows={4}
              maxLength={900}
              className="w-full resize-y rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-black outline-none ring-primary/40 transition focus:ring-2"
              placeholder={ui.messagePlaceholder}
              {...register("message")}
            />
          </label>
        </>
      )}
    />
  );
}
